/**
 * Maryland (MDCRIS, ADR-073) — the coded Expense Category → Purpose picker for the expenditure form.
 *
 * MDCRIS files a disbursement under a coded (Category, Purpose) pair and rejects an uploaded row whose
 * pair isn't a listed combination. A ~106-option flat purpose list is unusable, so the treasurer first
 * picks an Expense Category, then a Purpose from a searchable list FILTERED to that category. The
 * purpose options come only from the chosen category's `purposes`, so an out-of-cascade pair is
 * structurally unpresentable — the picker cannot produce an invalid pair. Changing the category drops a
 * now-illegal purpose rather than letting it survive.
 *
 * Both halves are the same searchable combobox (one shared `StringCombobox` below, built on the same
 * Popover + cmdk primitives as {@link ContactCombobox} / ReportTypeCombobox) — the category is
 * searchable too so the two controls stay consistent.
 *
 * The cascade IS the checked-in `MdExpensePurposeAllowList` (fetched, never re-transcribed on the
 * client). An unclassified row is legitimate — it files under the safe `Other Expenses / Other` default
 * — so the empty state shows that default with a nudge to refine, and never blocks the save.
 */
import { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import type { MdExpenseCategoryGroupDto } from '@signalvote/shared';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';

interface ComboItem {
  /** The committed value; `''` is the "not classified" clear item. */
  value: string;
  label: string;
}

interface StringComboboxProps {
  id: string;
  testId: string;
  label: string;
  /** The current value, or `''` when nothing is chosen. */
  value: string;
  items: ComboItem[];
  onSelect: (value: string) => void;
  placeholder: string;
  searchPlaceholder: string;
  emptyText: string;
}

/**
 * A searchable single-string combobox shared by both halves of the picker. Owns its own
 * case-insensitive substring filter (`shouldFilter={false}`) so search is predictable over the exact
 * items passed in — for the purpose half those items are the only legal purposes, so the pair can
 * never go out-of-cascade.
 */
function StringCombobox({
  id,
  testId,
  label,
  value,
  items,
  onSelect,
  placeholder,
  searchPlaceholder,
  emptyText,
}: StringComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const selected = items.find((i) => i.value === value && i.value !== '');
  const q = query.trim().toUpperCase();
  const filtered = q ? items.filter((i) => i.label.toUpperCase().includes(q)) : items;

  const commit = (next: string) => {
    onSelect(next);
    setQuery('');
    setOpen(false);
  };

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium mb-1">
        {label}
      </label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            id={id}
            data-testid={testId}
            type="button"
            role="combobox"
            aria-expanded={open}
            className={cn(
              'flex w-full items-center justify-between rounded-md border px-3 py-2 text-sm',
              !selected && 'text-muted-foreground',
            )}
          >
            <span className="truncate">{selected ? selected.label : placeholder}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0"
          align="start"
          onClick={(e) => e.stopPropagation()}
        >
          <Command shouldFilter={false}>
            <CommandInput placeholder={searchPlaceholder} value={query} onValueChange={setQuery} />
            <CommandList>
              {filtered.length === 0 && <CommandEmpty>{emptyText}</CommandEmpty>}
              {filtered.length > 0 && (
                <CommandGroup>
                  {filtered.map((i) => (
                    // cmdk wants a non-empty item value; the "not classified" clear item is keyed
                    // separately but still commits `''`.
                    <CommandItem
                      key={i.value || '__none__'}
                      value={i.value || '__none__'}
                      onSelect={() => commit(i.value)}
                    >
                      <Check
                        className={cn('mr-2 h-4 w-4', value === i.value ? 'opacity-100' : 'opacity-0')}
                      />
                      <span className="truncate">{i.label}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

interface MdExpensePurposePickerProps {
  /** The filer-selectable cascade (from `useMdExpensePurposeCascade`). */
  cascade: MdExpenseCategoryGroupDto[];
  /** The chosen MDCRIS category, or null when unclassified. */
  category: string | null;
  /** The chosen MDCRIS purpose, or null. */
  purpose: string | null;
  /** Reports the coded pair; a null category always carries a null purpose (unclassified). */
  onChange: (category: string | null, purpose: string | null) => void;
  /** Ties the category control to an external layout; the purpose control derives its own id. */
  id?: string;
}

export function MdExpensePurposePicker({
  cascade,
  category,
  purpose,
  onChange,
  id,
}: MdExpensePurposePickerProps) {
  const categoryId = id ?? 'md-expense-category';
  const selectedGroup = cascade.find((g) => g.category === category) ?? null;

  const categoryItems: ComboItem[] = [
    { value: '', label: '— Not classified —' },
    ...cascade.map((g) => ({ value: g.category, label: g.category })),
  ];
  const purposeItems: ComboItem[] = (selectedGroup?.purposes ?? []).map((p) => ({
    value: p,
    label: p,
  }));

  const handleCategoryChange = (nextCategory: string) => {
    if (nextCategory === '') {
      onChange(null, null);
      return;
    }
    // Keep the purpose only if it is still legal under the new category — otherwise drop it so the
    // pair can never go out-of-cascade.
    const group = cascade.find((g) => g.category === nextCategory);
    const keptPurpose = purpose && group?.purposes.includes(purpose) ? purpose : null;
    onChange(nextCategory, keptPurpose);
  };

  return (
    <div className="space-y-2">
      <StringCombobox
        id={categoryId}
        testId="md-category-combobox"
        label="MDCRIS expense category"
        value={category ?? ''}
        items={categoryItems}
        onSelect={handleCategoryChange}
        placeholder="Select a category…"
        searchPlaceholder="Search categories…"
        emptyText="No matching categories."
      />

      {category ? (
        <StringCombobox
          id="md-purpose-combobox"
          testId="md-purpose-combobox"
          label="MDCRIS purpose"
          value={purpose ?? ''}
          items={purposeItems}
          onSelect={(p) => onChange(category, p)}
          placeholder="Select a purpose…"
          searchPlaceholder="Search purposes…"
          emptyText="No matching purposes."
        />
      ) : (
        <p data-testid="md-purpose-unclassified" className="text-xs text-muted-foreground">
          Unclassified — files under the <span className="font-medium">Other Expenses / Other</span>{' '}
          default. Choose a category to refine the MDCRIS classification.
        </p>
      )}
    </div>
  );
}
